import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { EQ, CCD, CCPS } from "../../../utils/constants/constants";
// Unit type options for the dropdown
const unitTypeOptions = [
  { value: CCPS, label: "CCPS" },
  { value: CCD, label: "CCD" },
  { value: EQ, label: "EQ" },
];

/**
 * DealBookingForm Component
 * 
 * Form for creating a new deal booking.
 * Handles input, validation, and submission.
 * 
 * @param {Object} props - Component props
 * @param {string} props.schemeName - Name of the selected scheme
 * @param {string} props.companyLegalName - Legal name of the company
 * @param {Date} props.postingDate - Selected posting date
 * @param {string} props.purchaseQuantity - Purchase quantity value
 * @param {string} props.purchaseRate - Purchase rate value
 * @param {string} props.unitType - Selected unit type
 * @param {boolean} props.purchaseQuantityError - Whether there's an error in purchase quantity
 * @param {boolean} props.purchaseRateError - Whether there's an error in purchase rate
 * @param {boolean} props.unitTypeError - Whether there's an error in unit type
 * @param {boolean} props.isLoading - Whether data is being loaded
 * @param {boolean} props.isSaving - Whether form is being saved
 * @param {boolean} props.isFormDirty - Whether form has unsaved changes
 * @param {Function} props.onFieldChange - Callback when a field changes
 * @param {Function} props.onSave - Callback when the form is submitted
 */
const DealBookingForm = ({
  schemeName,
  companyLegalName,
  postingDate,
  purchaseQuantity,
  purchaseRate,
  unitType,
  purchaseQuantityError,
  purchaseRateError,
  unitTypeError,
  isLoading,
  isSaving,
  isFormDirty,
  onFieldChange,
  onSave
}) => {
  return (
    <div className="card flex-grow-1 p-3">
      <Stack direction="row" spacing={2}>
        <TextField
          id="schemeName"
          name="schemeName"
          label="Scheme Name"
          variant="outlined"
          value={schemeName || ""}
          disabled={true}
          fullWidth
          size="small"
        />
        <TextField
          id="companyLegalName"
          name="companyLegalName"
          label="Company Legal Name"
          variant="outlined"
          value={companyLegalName || ""}
          disabled={true}
          fullWidth
          size="small"
        />
      </Stack>
      <Stack sx={{ marginTop: "1rem" }} direction="row" spacing={2}>
      </Stack>
      <Stack sx={{ flex: 1 }} direction="column" spacing={2}>
        <Stack sx={{ marginBottom: "2rem" }} direction="row" spacing={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Posting Date"
              value={postingDate}
              onChange={(date) => {
                if (date) {
                  onFieldChange('postingDate', date);
                }
              }}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            />
          </LocalizationProvider>

          <TextField
            id="purchaseQuantity"
            name="purchaseQuantity"
            label="Purchase Quantity (Units)"
            variant="outlined"
            type="number"
            value={purchaseQuantity}
            onChange={(e) => onFieldChange('purchaseQuantity', e.target.value)}
            fullWidth
            required
            error={purchaseQuantityError}
            helperText={purchaseQuantityError ? "Valid Purchase Quantity is required" : ""}
            InputProps={{ inputProps: { min: 1 } }}
            size="small"
          />
        </Stack>

        <Stack sx={{ flex: 1 }} direction="row" spacing={2}>
          <TextField
            id="purchaseRate"
            name="purchaseRate"
            label="Purchase Rate per Unit"
            variant="outlined"
            type="number"
            value={purchaseRate}
            onChange={(e) => onFieldChange('purchaseRate', e.target.value)}
            fullWidth
            required
            error={purchaseRateError}
            helperText={purchaseRateError ? "Valid Purchase Rate is required" : ""}
            InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
            size="small"
          />

          <FormControl fullWidth error={unitTypeError} size="small">
            <InputLabel id="unitType-label">Unit Type</InputLabel>
            <Select
              labelId="unitType-label"
              id="unitType"
              name="unitType"
              value={unitType}
              label="Unit Type"
              onChange={(e) => onFieldChange('unitType', e.target.value)}
              required
            >
              {unitTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {unitTypeError && <p className="MuiFormHelperText-root Mui-error">Please select a unit type</p>}
          </FormControl>
        </Stack>

        <div className="d-flex align-items-end">
          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSave();
            }}
            disableElevation
            disabled={isSaving || !isFormDirty}
          >
            {isLoading && !isSaving ? "Loading..." : (isSaving ? "Saving..." : "Save")}
          </Button>
        </div>
      </Stack>
    </div>
  );
};

DealBookingForm.propTypes = {
  schemeName: PropTypes.string.isRequired,
  companyLegalName: PropTypes.string.isRequired,
  postingDate: PropTypes.instanceOf(Date).isRequired,
  purchaseQuantity: PropTypes.string.isRequired,
  purchaseRate: PropTypes.string.isRequired,
  unitType: PropTypes.string.isRequired,
  purchaseQuantityError: PropTypes.bool.isRequired,
  purchaseRateError: PropTypes.bool.isRequired,
  unitTypeError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isFormDirty: PropTypes.bool.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default DealBookingForm;